import React, { useCallback, useContext, useEffect, useState } from 'react';
import cn from 'classnames';
import Dropdown from 'components/Common/Dropdown/Dropdown';
import getSelectedAttributeValue from 'utils/attributes/getSelectedAttributeValue';
import { useTranslation } from 'react-i18next';
import { STOCK_STATUS } from 'store/product/types';
import { ExtendedProductPageSelectorOption } from 'utils/attributes/exploreOptions';
import Drawer from 'components/Common/Drawer/Drawer';
import cleanupLocationHash from 'utils/cleanupLocationHash';
import getHashFromPath from 'utils/getHashFromPath';
import AttributeSelectorPlacementContext, {
  AttributeSelectorPlacement,
} from 'context/ProductAttributesPlacementContext';
import gtmSendEvent from 'utils/gtm/gtmSendEvent';
import gtmProductPageInteractionEvent from 'utils/gtm/gtmProductPageInteractionEvent';
import { ProductAttributeSelectorProps } from '../ProductAttributeSelector';
import styles from './ProductAttributeDropdownSelectorPremium.module.scss';

const SELECTOR_HASH = '#selector';

const ProductAttributeDropdownSelectorPremium = ({
  selector,
  options,
  onClick,
  theme,
}: ProductAttributeSelectorProps) => {
  const { t } = useTranslation();
  const [showOptions, setShowOptions] = useState(false);
  const placementContext = useContext(AttributeSelectorPlacementContext);
  const selecotorInsidePopup = placementContext === AttributeSelectorPlacement.POPUP;

  if (!options.some((option) => !option.disabled)) {
    return null;
  }

  const hideDrawer = () => {
    setShowOptions(false);
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, option: ExtendedProductPageSelectorOption) => {
    if (option.avalilabilityStatus?.status === STOCK_STATUS.STATUS_UNAVAILABLE) {
      e.stopPropagation();
      return;
    }
    gtmSendEvent(gtmProductPageInteractionEvent('Dropdown', selector.typeName, option.name));
    onClick(selector.typeName, option.value, option.productId);
    hideDrawer();
  };

  const displaySingleSelectorPopup = () => {
    if (selecotorInsidePopup) {
      setShowOptions(true);
    }
  };

  const onHashChange = useCallback(
    (e: any) => {
      if (getHashFromPath(e.oldURL) === SELECTOR_HASH) {
        hideDrawer();
      }
    },
    [hideDrawer],
  );

  useEffect(() => {
    if (showOptions) {
      window.location.hash = SELECTOR_HASH;
      window.addEventListener('hashchange', onHashChange, false);
      return () => {
        window.removeEventListener('hashchange', onHashChange);
        cleanupLocationHash(SELECTOR_HASH);
      };
    }
    return () => {};
  }, [showOptions]);

  const renderOptions = () =>
    options.map(
      (option) =>
        !option.disabled && (
          <div
            data-type="product-page-selector-option"
            data-attribute-type-name={selector.name}
            data-attribute-value={option.value}
            data-attribute-name={option.name}
            key={`dd${option.value}`}
            className={cn(styles.option, {
              [styles.selected]: option.selected,
              [styles.disabled]:
                option.disabled || option.avalilabilityStatus?.status === STOCK_STATUS.STATUS_UNAVAILABLE,
            })}
            onClick={(event) => handleClick(event, option)}
          >
            <div className={styles.content}>
              {option.name}
              {option.avalilabilityStatus?.status === STOCK_STATUS.STATUS_UNAVAILABLE && (
                <span> · {t('txt_availability_status_unavailable')}</span>
              )}
            </div>
          </div>
        ),
    );

  return (
    <div
      className={cn(styles.dropdownSelector, styles[`theme-${theme}`], {
        [styles.inPopup]: selecotorInsidePopup,
      })}
    >
      <div className={styles.selectorName}>{selector.name}</div>
      <div className={cn(styles.options, styles.selectorPopup)} onClick={displaySingleSelectorPopup}>
        <Dropdown
          value={getSelectedAttributeValue(options) || ''}
          expandable={!selecotorInsidePopup}
          classNames={{
            dropdown: styles.dropdown,
            currentValue: styles.dropdownCurrentValue,
            options: styles.dropdownOptions,
          }}
        >
          {renderOptions()}
        </Dropdown>
      </div>

      {showOptions && (
        <Drawer closeIconInContent contentClassName={styles.drawerContent} onClose={hideDrawer}>
          <div className={styles.drawerContentWrapper}>
            <div className={styles.selectorName}>{selector.name}</div>
            <div className={styles.dropdownOptions}>
              <div className={styles.options}>{renderOptions()}</div>
            </div>
          </div>
        </Drawer>
      )}
    </div>
  );
};

export default ProductAttributeDropdownSelectorPremium;
